import { useCallback, useMemo } from 'react';
import { useFeatureFlags, useLoggedInUser, useProductAssetsByUser } from '@tcc/shared-data-lib';
import { useLocation } from 'react-router-dom';
import { useFeatureFlagsConfigs, useProductAssetsByUserConfigs, useUserLoggedInConfigs } from '../../utils/sharedData';
import { useImpersonationMode } from '../useImpersonationMode';
import { byAllowedContryCodes, byAllowedFeatureAssets, byAllowedImpesonationModes, byAllowedRoles, byExcludedByFeaureFlag, byExcludedContryCodes, byExcludededRoles, byExcludedImpersonationModes, byRequiredFeatureFlags } from './filters';
import { descriptorToNavigationLink, toExperimentalSpaLinks } from './mappers';
import { useLinkDescriptors } from './useLinkDescriptors';
import { LinkDescriptor } from './types';

export const useNavigationLinks = () => {
  const location = useLocation();
  const { user } = useLoggedInUser(useUserLoggedInConfigs);
  const { data: assets, isLoading: isLoadingAssets } = useProductAssetsByUser(useProductAssetsByUserConfigs(user));
  const { featureFlags, isLoading: isLoadingFeatureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const { impersonationModes, isLoading: isLoadingModes } = useImpersonationMode();
  const { linkDescriptors, menuDescriptors } = useLinkDescriptors();

  const isLoading = !user || isLoadingAssets || isLoadingFeatureFlags || isLoadingModes;

  const filterLinks = useCallback((links: LinkDescriptor[]) => links.filter(descriptor => byAllowedRoles(user.details.roles, descriptor))
    .filter(descriptor => byExcludededRoles(user.details.roles, descriptor))
    .filter(descriptor => byAllowedContryCodes(user.details.countryCode, descriptor))
    .filter(descriptor => byExcludedContryCodes(user.details.countryCode, descriptor))
    .filter(descriptor => byAllowedFeatureAssets(assets.userSubscription.featuresByUser, descriptor))
    .filter(descriptor => byRequiredFeatureFlags(featureFlags, descriptor))
    .filter(descriptor => byExcludedByFeaureFlag(featureFlags, descriptor))
    .filter(descriptor => byAllowedImpesonationModes(impersonationModes, descriptor))
    .filter(descriptor => byExcludedImpersonationModes(impersonationModes, descriptor)), [assets, featureFlags, impersonationModes, user]);

  const links = useMemo(() => {
    if (isLoading) return [];
    return filterLinks(linkDescriptors)
      .map(descriptor => descriptorToNavigationLink(descriptor, location))
      .map(toExperimentalSpaLinks);
  }, [isLoading, linkDescriptors, filterLinks, location]);

  const menues = useMemo(() => {
    if (isLoading) return [];
    return menuDescriptors
      .map(menuDescriptor => {
        const links = filterLinks(menuDescriptor.links);

        return {
          ...menuDescriptor,
          links: links.map(descriptor => descriptorToNavigationLink(descriptor, location))
            .map(toExperimentalSpaLinks)
        };
      }).filter(menuDescriptor =>
        // All links in this menu were filtered out, do not show it.
        menuDescriptor.links.length
      );
  }, [isLoading, menuDescriptors, filterLinks, location]);

  return {
    isLoading,
    links,
    menues,
  };
};
